import { useSubjectMember } from '@healthblocks-io/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { isNil } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { ConfirmationModal } from '@/@bothrs-ui/web/components/confirmation-modal/ConfirmationModal'
import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { globalDefinitions } from '@/defined-translations'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { DetailPageLayout } from '@/layout/components/detail-page-layout/DetailPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { mixpanel } from '@/mixpanel'
import { AgendaContactSection } from '@/modules/shared/components/agenda-detail/AgendaContactSection'
import { AgendaDetail } from '@/modules/shared/components/agenda-detail/AgendaDetail'
import { AgendaMoment } from '@/modules/shared/components/agenda-detail/AgendaMoment'
import { AgendaNotes } from '@/modules/shared/components/agenda-detail/AgendaNotes'
import { AgendaLocationCard } from '@/modules/shared/components/agenda-location-card/AgendaLocationCard'
import { CancelAppointmentModal } from '@/modules/shared/components/cancel-appointment-modal/CancelAppointmentModal'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { formatAgendaDate, formatAgendaTime } from '@/modules/shared/helpers/agenda-moment'
import { resolveAgendaContact } from '@/modules/shared/helpers/contact'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useNavigateToLocation } from '@/modules/shared/hooks/useNavigateToLocation'
import { useToast } from '@/modules/shared/hooks/useToast'
import { RelatedItems } from '@/modules/timeline/components/related-items/RelatedItems'
import { useRelatedItems } from '@/modules/timeline/hooks/useRelatedItems'
import { activityTypeTranslations } from '@/modules/timeline/types/translations'
import { copyToClipboard } from '@/utils/clipboard'
import { formatToWaitTime } from '@/utils/time'
import { CopyableAppointmentNumber } from '../../components/appointment-number/CopyableAppointmentNumber'
import { DoctorInfo } from '../../components/doctor-info/DoctorInfo'
import { NoShowNotice } from '../../components/no-show-notice/NoShowNotice'
import { WaitlistSection } from '../../components/waitlist-section/WaitlistSection'
import { canCancelAppointment } from '../../helpers/cancellation'
import { getAppointmentLocation } from '../../helpers/location'
import { isPastAppointment } from '../../helpers/past'
import { canShowWaitlist } from '../../helpers/waitlist'
import { useAppointmentWaitlist } from '../../hooks/useAppointmentWaitlist'

import type { TimelineContextType } from '@/modules/timeline/types/timeline-context.type'
import type { Appointment } from '../../types/appointment.types'

const AppointmentDetailPage = () => {
  const navigate = useNavigate()
  const { appointments, forceReload, isLoading, patchAppointment } = useOutletContext<TimelineContextType>()

  const theme = useTheme()
  const subjectMember = useSubjectMember()
  const { id } = useParams()

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false)
  const [isCancellingAppointment, setIsCancellingAppointment] = useState<boolean>(false)

  // Hide navigation on native devices
  useHideNativeNavigation()

  // Template variables
  // ------------------------------------------------------------------------- /
  const appointment: Appointment | undefined = useMemo(() => {
    if (appointments.value && id) {
      return appointments.value.find((appointment: Appointment) => appointment.id.toString() === id)
    }
  }, [appointments.value, id])

  const location = useMemo(() => getAppointmentLocation(appointment), [appointment])
  // The section shows only for an item with a trigger; the query keeps the trigger as stored.
  const related = useRelatedItems(appointment?.trigger.trim() ? appointment.trigger : undefined)

  const waitlist = useAppointmentWaitlist(appointment, patchAppointment)
  const handleNavigate = useNavigateToLocation('appointment_location_navigate')
  const { showSuccessToast } = useToast()

  const careplan = appointment?.activities?.at(0)?.careplan?.display

  const appointmentNumber = appointment?.meta?.appointmentNumber

  const appointmentTitle = i18n._(activityTypeTranslations.Appointment as string)
  const specialisation = appointment?.meta?.esign?.specialisation?.title.nl

  const dateString = appointment?.start ? formatAgendaDate(appointment.start) : undefined
  const startTime = appointment?.start ? formatAgendaTime(appointment.start) : undefined
  const waitTime = appointment?.meta?.synops?.waitTime

  // Past appointments keep their facts and related items; the actions (contact, waitlist, cancel)
  // and the kiosk hint go. The cancel deadline below is a separate, stricter rule.
  const isPast = appointment ? isPastAppointment(appointment.start) : false

  const notes = appointment?.meta?.notes ?? []
  const noShow = appointment?.meta?.noShow === true

  const { name: contactName, phone } = resolveAgendaContact(location, appointment?.meta?.esign?.contactInfo)

  // The no-show notice tells the patient to contact the secretariat, so the phone card stays with
  // it even though the appointment is past.
  const showContactCard = !isPast || noShow
  // The heading points to the secretariat to cancel or change, which no longer applies once the
  // appointment is past or cancelled.
  const showContactHeading = !isPast && appointment?.status !== 'cancelled'

  const showWaitlist = appointment ? canShowWaitlist(appointment) : false

  // How the appointment reads inside a sentence, e.g. "donderdag 13 augustus om 08:45".
  // Translated as a whole: joining the date and the time with a hardcoded "om"
  // left a Dutch word inside an English or French sentence.
  const appointmentMoment =
    appointment?.start && startTime
      ? t({
          id: 'appointments.detail.moment',
          comment: 'An appointment written inside a sentence, e.g. "donderdag 13 augustus om 08:45"',
          message: '{date} om {time}',
          values: { date: formatLocale(new Date(appointment.start), 'EEEE d MMMM'), time: startTime },
        })
      : ''

  // Cancelling is possible up to the deadline before the start; outside that window the
  // patient is pointed to the secretariat instead of being shown a disabled button.
  const canCancel = appointment ? canCancelAppointment(appointment) && !isCancellingAppointment : false

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  function handleCancelIntentAppointment() {
    mixpanel.track('appointment_cancel_intent', { origin: 'detail' })
    setShowCancelModal(true)
  }

  function handleCloseModal() {
    forceReload()
    setShowCancelModal(false)
  }

  function handleCloseAfterCancel() {
    forceReload()
    setShowCancelModal(false)
    navigate(-1)
  }

  function handleIsCancelling() {
    if (appointment) setIsCancellingAppointment(true)
  }

  function handleBookAppointment() {
    navigate('/appointment')
  }

  // Nothing is shown when the copy fails: the platform already reports that itself.
  async function handleCopyAppointmentNumber() {
    if (!appointmentNumber) {
      return
    }

    mixpanel.track('appointment_number_copy')

    if (await copyToClipboard(appointmentNumber)) {
      showSuccessToast({
        message: t({
          id: 'appointments.detail.appointment-number.copied',
          message: 'Afspraaknummer gekopieerd',
        }),
      })
    }
  }

  // Effects
  // ------------------------------------------------------------------------- /
  // A cancellation that was under way belongs to the appointment it was started on.
  useEffect(() => {
    setIsCancellingAppointment(false)
  }, [appointment?.id])

  useEffect(() => {
    if (!appointment) {
      if (!appointments.value && !isLoading) {
        forceReload()
      } else if (appointments.value) {
        navigate('/timeline')
      }
    }
  }, [appointments, appointment, forceReload, isLoading, navigate])

  // Render Methods
  // ------------------------------------------------------------------------- /
  // The phone header and the content from M each show the title, so the page builds it once for both.
  const title = (
    <Flex justifyContent='space-between' alignItems='center'>
      <AgendaDetail.Title eyebrow={specialisation ? appointmentTitle : undefined} eyebrowIcon={['fal', 'calendar']}>
        {specialisation || appointmentTitle}
      </AgendaDetail.Title>
      {careplan ? <Chip type='Primary'>{careplan}</Chip> : null}
    </Flex>
  )

  const isLoadingList = appointments.isLoading && isNil(appointments.value)

  return (
    <>
      <ContentPageLayout backgroundColor={theme.Colors.Background.White} noPadding noScroll>
        <DetailPageLayout>
          {/* Stays while the list loads so the patient can still go back */}
          <DetailPageLayout.Header backLabel={i18n._(globalDefinitions.goBack)} onBack={() => navigate(-1)}>
            {title}
          </DetailPageLayout.Header>
          <DetailPageLayout.Content>
            {isLoadingList ? (
              <AnimatedLoader />
            ) : (
              <AgendaDetail>
                <DetailPageLayout.Title>{title}</DetailPageLayout.Title>
                {/* Doctor - Day - Time: one block, closer together than the sections around it */}
                <FlexColumn gap={theme.UI.Spacing.Space3}>
                  {appointment?.meta?.esign?.doctor ? (
                    <DoctorInfo>
                      <DoctorInfo.Avatar
                        images={appointment.meta.esign.doctor.images}
                        name={appointment.meta.esign.doctor.name}
                        size='large'
                      />
                      <DoctorInfo.Details>
                        <DoctorInfo.Name type='BodyLargeBold'>{appointment.meta.esign.doctor.name}</DoctorInfo.Name>
                        {specialisation ? <DoctorInfo.Description>{specialisation}</DoctorInfo.Description> : null}
                        {appointment.meta.esign.doctor.conventionStatus?.nl ? (
                          <DoctorInfo.Note>{appointment.meta.esign.doctor.conventionStatus.nl}</DoctorInfo.Note>
                        ) : null}
                      </DoctorInfo.Details>
                    </DoctorInfo>
                  ) : null}

                  <AgendaMoment
                    date={dateString}
                    time={appointment?.meta?.hiddenInApp ? undefined : startTime}
                    waitTime={waitTime ? (formatToWaitTime(waitTime) ?? undefined) : undefined}
                  />
                </FlexColumn>
                {/* Location - Appointment number */}
                {location || appointmentNumber ? (
                  <AgendaDetail.Cards>
                    {location ? <AgendaLocationCard location={location} onNavigate={handleNavigate} /> : null}

                    {appointmentNumber ? (
                      <CopyableAppointmentNumber
                        number={appointmentNumber}
                        showHint={!isPast}
                        onCopy={handleCopyAppointmentNumber}
                      />
                    ) : null}
                  </AgendaDetail.Cards>
                ) : null}

                {/* Notes */}
                {noShow ? (
                  <>
                    <Line />
                    <NoShowNotice givenName={subjectMember?.givenName} />
                  </>
                ) : (
                  <AgendaNotes
                    title={t({
                      id: 'appointments.detail.about',
                      message: 'Over deze afspraak',
                    })}
                    notes={notes}
                  />
                )}

                {/* Contact. Changing an appointment always goes through the secretariat, so the section shows next to the cancel button too. */}
                {/* A past appointment shows the card for the no-show notice only, without the heading */}
                {showContactCard ? (
                  <AgendaContactSection
                    title={
                      showContactHeading
                        ? t({
                            id: 'appointments.detail.cancel-or-change',
                            comment: 'Cancel or change contact message',
                            message: 'Afspraak annuleren of wijzigen?',
                          })
                        : undefined
                    }
                    description={
                      showContactHeading
                        ? t({
                            id: 'appointments.detail.contact',
                            comment: 'Contact message for appointment detail',
                            message: 'Neem telefonisch contact op met het secretariaat.',
                          })
                        : undefined
                    }
                    name={contactName}
                    phone={phone}
                    onCall={() => mixpanel.track('appointment_contact', { type: 'phone', origin: 'detail' })}
                  />
                ) : null}

                {/* Waitlist */}
                {showWaitlist ? (
                  <WaitlistSection
                    toggleId='appointment-waitlist'
                    checked={waitlist.waitlisted}
                    onChange={waitlist.requestChange}
                    disabled={waitlist.isToggling}
                    description={t({
                      id: 'appointments.detail.waitlist.description',
                      comment: 'Explanation under the waitlist toggle on the appointment detail page',
                      message:
                        'Ik wil vroeger komen als er een plaats vrijkomt. De dienst belt je indien er een plaats vrijkomt die bij je past. Je huidige afspraak blijft in elk geval staan.',
                    })}
                    note={
                      waitlist.waitlisted ? (
                        <>
                          <Span type='BodyMediumBold'>
                            {t({
                              id: 'appointments.detail.waitlist.note.title',
                              comment: 'Note title on the appointment detail page once the waitlist request is on',
                              message: 'Je vraag staat genoteerd.',
                            })}
                          </Span>
                          <Span type='BodyMediumRegular'>
                            {t({
                              id: 'appointments.detail.waitlist.note.body',
                              comment: 'Note body on the appointment detail page once the waitlist request is on',
                              message: 'Hoor je niets, dan blijft je afspraak van {appointmentMoment} gewoon staan.',
                              values: { appointmentMoment },
                            })}
                          </Span>
                        </>
                      ) : undefined
                    }
                  />
                ) : null}

                {/* Related triggers */}
                <RelatedItems {...related} />
              </AgendaDetail>
            )}
          </DetailPageLayout.Content>
          {/* While a cancellation runs, or once Synops reports it, the footer says so instead of offering it */}
          {!isLoadingList && (appointment?.status === 'cancelled' || isCancellingAppointment) ? (
            <DetailPageLayout.Footer>
              <AgendaDetail.Text>
                {t({
                  id: 'appointments.detail.disable-cancelled',
                  comment: 'Cancelled description',
                  message: 'We zijn je afspraak aan het annuleren. Dit kan enkele minuten duren.',
                })}
              </AgendaDetail.Text>
            </DetailPageLayout.Footer>
          ) : null}
          {/* No disabled button once cancelling is no longer possible: the contact section above says what to do */}
          {!isLoadingList && canCancel ? (
            <DetailPageLayout.Footer>
              <Button variant='Secondary' onClick={handleCancelIntentAppointment}>
                {t({
                  id: 'appointments.detail.tertiary',
                  comment: 'Tertiary button text on the appointment detail page',
                  message: 'Afspraak annuleren',
                })}
              </Button>
            </DetailPageLayout.Footer>
          ) : null}
        </DetailPageLayout>
      </ContentPageLayout>

      {/*
        Switching the waitlist request on is silent — nothing is lost by it —
        but switching it off gives up a place the patient asked for, so it is
        worth one question.
      */}
      <ConfirmationModal
        isVisible={waitlist.showWithdrawModal}
        isLoading={waitlist.isToggling}
        onAction={waitlist.confirmWithdraw}
        onClose={waitlist.keep}
        title={t({
          id: 'appointments.modal.waitlist-withdraw.title',
          comment: 'Title of the modal asking to confirm withdrawing the waitlist request',
          message: 'Toch niet vroeger komen?',
        })}
        body={t({
          id: 'appointments.modal.waitlist-withdraw.body',
          comment: 'Body of the modal asking to confirm withdrawing the waitlist request',
          message:
            'We halen je vraag weg. De dienst belt je dan niet meer over een vroegere afspraak. Je afspraak van {appointmentMoment} blijft staan.',
          values: { appointmentMoment },
        })}
        actionButtonText={t({
          id: 'appointments.modal.waitlist-withdraw.primary',
          comment: 'Primary button of the withdraw waitlist modal',
          message: 'Vraag intrekken',
        })}
        cancelButtonText={t({
          id: 'appointments.modal.waitlist-withdraw.secondary',
          comment: 'Secondary button of the withdraw waitlist modal, keeps the request',
          message: 'Behouden',
        })}
      />

      <CancelAppointmentModal
        key={appointment?.id}
        appointment={appointment}
        isVisible={showCancelModal}
        onAction={handleBookAppointment}
        onClose={handleCloseModal}
        onCloseAfterCancel={handleCloseAfterCancel}
        onCancelling={handleIsCancelling}
      />
    </>
  )
}

export { AppointmentDetailPage }
